'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { ShowListRecord } from '@bip/api-client';
import { IMG } from '../../imgPrefix';
import { getStarValue, formatReviewCount } from '../../_lib/shows/reviewFormat';
import { useCurrency } from '../../_components/CurrencyProvider';
import { SHOWS_BASE_PATH } from '../config';

const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;

// Local image-with-fallback — deliberately NOT importing Sports'
// `FallbackImage` component (app/sports/_components/) or Hotels' —
// route isolation forbids importing another domain's `_components`.
function ShowCardImage({ src, alt }: { src: string | null; alt: string }) {
  const [currentSrc, setCurrentSrc] = useState(src || FALLBACK_IMAGE);
  useEffect(() => setCurrentSrc(src || FALLBACK_IMAGE), [src]);
  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (currentSrc !== FALLBACK_IMAGE) setCurrentSrc(FALLBACK_IMAGE);
      }}
      className="h-full w-full object-cover"
    />
  );
}

/**
 * Event Card — DOCS/shows/listing/brd-frontend.md §4, styled/positioned to
 * match `theaters-cms-reference-code/pages/Shows.html`'s `.showlist` card
 * markup: 4-corner notch (`.shows-card`, same clip-path technique as
 * `NearbyShowCard.css`'s `.rnsh-card`), category tag as a pill overlaid at
 * the image's BOTTOM-left (`.theatrecategotytext`/`.purple-button`, not a
 * top badge), and the discount ribbon inline beside the price at the
 * bottom of the card body (`.ribbon-btn`, not an image overlay).
 *
 * Every element/rule is cross-referenced to brd-frontend.md §4:
 *  - image fallback chain: resolved server-side (ShowListRecord.image)
 *  - rating/reviews: §4.1 + §4.3 visibility matrix
 *  - tagline: full text, no truncation (§4.1); row-height matching is a
 *    CSS grid concern handled by the parent grid, not this component. Shows
 *    `show.tagLine` (LTD's short marketing line), NOT `show.description`
 *    (the long synopsis) — matches the legacy CMS's own `initiate-theatre-
 *    search.js`, which projects `TagLine` (not `Description`) for its
 *    listing/search card.
 *  - price / discount badge: hidden entirely when absent, not shown as 0
 *  - category tag: same color for every category (§4.1) — no per-category coding
 *  - entire card clickable → new tab (§4.1)
 */
export function ShowCard({
  show,
  dateFrom,
  bookingDate,
}: {
  show: ShowListRecord;
  dateFrom?: string | null;
  bookingDate?: string | null;
}) {
  const { formatPrice } = useCurrency();
  const hasRating = show.rating != null && show.rating > 0;
  const hasReviews = show.reviewCount != null && show.reviewCount > 0;

  const starValue = hasRating ? getStarValue(show.rating!) : 0;
  const fullStars = Math.floor(starValue);
  const hasHalfStar = starValue % 1 !== 0;

  // Carries the listing's active date filter through to the detail page's
  // calendar (`?bookingDate=`, the same param `ShowDetailClient` reads and
  // `NearbyShowCard`'s `bookHref()` already uses) — without this, clicking a
  // show found via a date filter lost that date entirely on the way to the
  // detail page's calendar.
  const targetBookingDate = bookingDate || dateFrom;
  const detailHref = targetBookingDate
    ? `${SHOWS_BASE_PATH}/${show.slug}?bookingDate=${encodeURIComponent(targetBookingDate)}`
    : `${SHOWS_BASE_PATH}/${show.slug}`;

  return (
    <Link
      href={detailHref}
      target="_blank"
      rel="noopener noreferrer"
      prefetch={false}
      className="shows-card transition-shadow hover:shadow-lg"
    >
      <div className="shows-card-img-wrap">
        <ShowCardImage src={show.image} alt={show.name} />
        <div className='theatrecategotytext'>
          {show.category && <span className="shows-card-category">{show.category}</span>}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-4 show-card-lst">
        <h3 className="font-bold text-secondary title mb-0">{show.name}</h3>

        {/* No fallback "No ratings yet"/"No reviews yet" text — the review section is hidden
            entirely (not rendered at all) when a show has neither a rating nor a review count,
            per explicit requirement. */}
        {(hasRating || hasReviews) && (
          <div className="flex items-center gap-1.5 text-sm">
            {hasRating ? (
              <>
                <span className="font-semibold text-orange rating-txt">{show.rating!.toFixed(1)}</span>
                <span className="flex">
                  {Array.from({ length: 5 }, (_, i) => {
                    if (i < fullStars) return <i key={i} className="fas fa-star text-orange pl-1" />;
                    if (i === fullStars && hasHalfStar) return <i key={i} className="fas fa-star-half-alt text-orange pl-1" />;
                    return <i key={i} className="fal fa-star text-orange pl-1" />;
                  })}
                </span>
                {hasReviews && <span className="text-gray-text text-15">{formatReviewCount(show.reviewCount!)}</span>}
              </>
            ) : (
              <span className="text-gray-text">{formatReviewCount(show.reviewCount!)}</span>
            )}
          </div>
        )}

        {show.tagLine && (
          <p className="line-clamp-3">{show.tagLine}</p>
        )}

        {/* Price + discount ribbon, side by side — matches the CMS's
            `.d-flex.align-items-center` price row with `.ribbon-btn` inline. */}
        <div className="mt-auto">
          {show.price > 0 && (
            <h3 className="text-primary owners-font h5">
              From {formatPrice(show.price, show.currency)}
            </h3>
          )}
          {/* {show.discountPercent != null && (
            <span className="shows-card-ribbon">Save Up {show.discountPercent}%</span>
          )} */}
        </div>
      </div>
    </Link>
  );
}
